import { type ReactNode, useRef, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

interface TabDef {
  path: string
  element: ReactNode
}

/**
 * Renders all known tabs in parallel, toggling visibility via CSS.
 * Each tab mounts lazily (on first visit) and stays in DOM forever,
 * so component state (including generation progress) is preserved
 * across tab switches.
 *
 * Returns `null` for the matched content if the current path
 * doesn't match any tab (caller should render a fallback).
 */
export function TabLayout({
  tabs,
  fallback,
}: {
  tabs: readonly TabDef[]
  fallback: ReactNode
}) {
  const { pathname } = useLocation()

  // Track which tabs have been visited at least once
  const visited = useRef(new Set<string>())

  // Track previous pathname to trigger animation on tab switch
  const [prevPathname, setPrevPathname] = useState(pathname)
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    if (pathname !== prevPathname) {
      setIsTransitioning(true)
      setPrevPathname(pathname)
      const timer = requestAnimationFrame(() => {
        setIsTransitioning(false)
      })
      return () => cancelAnimationFrame(timer)
    }
  }, [pathname, prevPathname])

  const knownPath = tabs.some((t) => t.path === pathname)
  if (knownPath) {
    visited.current.add(pathname)
  }

  return (
    <>
      {tabs.map((tab) => {
        const isActive = pathname === tab.path
        const hasBeenVisited = visited.current.has(tab.path)

        // Don't mount until first visit
        if (!hasBeenVisited) return null

        return (
          <div
            key={tab.path}
            style={{ display: isActive ? 'contents' : 'none' }}
          >
            <div className={isActive && isTransitioning ? 'animate-fade-in' : ''}>
              {tab.element}
            </div>
          </div>
        )
      })}

      {/* Fallback (NotFound) for unknown paths */}
      {!knownPath && fallback}
    </>
  )
}
